import { useEffect, useRef, useState } from 'react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Button,
  CardActions,
  ClickAwayListener,
  Divider,
  Grid,
  Paper,
  Popper,
  useMediaQuery
} from '@mui/material';

// project imports
import MainCard from 'components/cards/MainCard';
import Transitions from 'components/assets/Transitions';

// assets
import FilterListIcon from '@mui/icons-material/FilterListTwoTone';

//types
import { FilterCategory } from 'models/table';
import FilterContent, { FilterStateType } from './FilterContent';
import { useAppSelector } from 'hooks/useAppStore';
import { selectAjaxLoader } from 'features/app/appSelectors';

type FilterProps = {
  categories: FilterCategory[];
  filterState?: FilterStateType;
  onFilterSubmit: (values: FilterStateType | undefined) => void;
};

const Filter = ({ categories, onFilterSubmit, filterState }: FilterProps) => {
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const operationInProgress = useAppSelector(selectAjaxLoader);

  const [open, setOpen] = useState(false);

  /**
   * anchorRef is used on different components and specifying one type leads to other components throwing an error
   * */
  const anchorRef = useRef<any>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (
    event: React.MouseEvent<HTMLDivElement> | MouseEvent | TouchEvent
  ) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const clearFilter = () => {
    onFilterSubmit(undefined);
    setOpen(false);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current && !open) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);

  return (
    <>
      <Button
        disableRipple
        onClick={handleToggle}
        color='secondary'
        startIcon={<FilterListIcon />}
        ref={anchorRef}
        disabled={operationInProgress}
      >
        {t(`common.table.filter`)}
      </Button>
      <Popper
        placement={matchesXs ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [matchesXs ? 5 : 0, 20]
            }
          }
        ]}
        sx={{ maxWidth: '200px' }}
      >
        {({ TransitionProps }) => (
          <ClickAwayListener onClickAway={handleClose}>
            <Transitions
              position={matchesXs ? 'top' : 'top-right'}
              in={open}
              {...TransitionProps}
            >
              <Paper>
                {open && (
                  <MainCard
                    border={false}
                    elevation={16}
                    content={false}
                    boxShadow
                    shadow={theme.shadows[16]}
                  >
                    <Grid container direction='column' spacing={1}>
                      <Grid item xs={1}>
                        <FilterContent
                          categories={categories}
                          filterState={filterState}
                          onSubmit={onFilterSubmit}
                        />
                      </Grid>
                    </Grid>
                    <Divider />
                    <CardActions sx={{ p: 1.25, justifyContent: 'center' }}>
                      <Button
                        size='small'
                        disableElevation
                        onClick={clearFilter}
                        disabled={operationInProgress}
                      >
                        {t(`common.table.clearFilter`)}
                      </Button>
                    </CardActions>
                  </MainCard>
                )}
              </Paper>
            </Transitions>
          </ClickAwayListener>
        )}
      </Popper>
    </>
  );
};

export default Filter;
